import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PurchaseSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!sessionId) {
      setState("failed");
      return;
    }

    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        // keep trying
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="grain bg-[#0B0C10] min-h-screen flex flex-col" data-testid="purchase-success-page">
      <Navbar />
      <section className="flex-1 grid place-items-center px-6 pt-32 pb-20">
        <div className="max-w-lg w-full text-center border border-[#1F222E] bg-[#12141A] p-10 md:p-14">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto text-[#D4AF37] animate-spin" size={44} />
              <h1 className="font-display text-3xl text-white font-light mt-6">Confirming your payment…</h1>
              <p className="text-[#9CA3AF] mt-3">Please hold on a moment.</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="mx-auto text-[#10B981]" size={52} />
              <h1 className="font-display text-3xl text-white font-light mt-6">Payment successful!</h1>
              <p className="text-[#9CA3AF] mt-3">
                Thank you for purchasing <span className="text-white">{order?.product_name}</span>. Your order is confirmed.
              </p>
              <p className="text-sm text-[#6B7280] mt-4">
                Digital editions are launching soon — we'll email your download to the address on your receipt the moment it's ready.
              </p>
              <Link to="/" className="inline-block mt-8 bg-[#D4AF37] text-[#0B0C10] font-medium hover:bg-[#F1D570] transition-colors rounded-sm px-8 py-3" data-testid="success-home">
                Back to home
              </Link>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle className="mx-auto text-[#EF4444]" size={52} />
              <h1 className="font-display text-3xl text-white font-light mt-6">
                {state === "timeout" ? "Still processing" : "Payment not completed"}
              </h1>
              <p className="text-[#9CA3AF] mt-3">
                {state === "timeout"
                  ? "We couldn't confirm your payment yet. If you were charged, you'll receive an email confirmation shortly."
                  : "Your payment was cancelled or didn't go through. You can try again anytime."}
              </p>
              <Link to="/#books" className="inline-block mt-8 border border-[#1F222E] text-white hover:border-[#D4AF37] transition-colors rounded-sm px-8 py-3" data-testid="success-retry">
                Back to books
              </Link>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
